// Locale detection & cookie management for depoia.
// Priority: cookie -> URL prefix -> Accept-Language -> Geo IP -> "pt".

export const SUPPORTED = ["pt", "en"];
export const DEFAULT_LOCALE = "pt";
const COOKIE = "depoia_locale";
const LEGACY_LS_KEY = "depoia_lang"; // migrated from previous localStorage flow
const COOKIE_MAX_AGE_DAYS = 365;

export function normalize(loc) {
  if (!loc) return null;
  const v = String(loc).toLowerCase();
  if (v.startsWith("pt")) return "pt";
  if (v.startsWith("en")) return "en";
  return null;
}

export function getCookieLocale() {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)depoia_locale=([^;]+)/);
  if (m) return normalize(decodeURIComponent(m[1]));
  return null;
}

export function setCookieLocale(loc) {
  if (typeof document === "undefined") return;
  const n = normalize(loc) || DEFAULT_LOCALE;
  const expires = new Date(Date.now() + COOKIE_MAX_AGE_DAYS * 24 * 3600 * 1000).toUTCString();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE}=${n}; path=/; expires=${expires}; SameSite=Lax${secure}`;
  try { localStorage.setItem(LEGACY_LS_KEY, n); } catch { /* noop */ }
}

// Reads the leading "/pt" or "/en" segment of the URL, if present.
export function getLocaleFromPath(pathname) {
  const m = /^\/(pt|en)(?=\/|$)/i.exec(pathname || "");
  return m ? normalize(m[1]) : null;
}

export function stripLocaleFromPath(pathname) {
  return (pathname || "").replace(/^\/(pt|en)(?=\/|$)/i, "") || "/";
}

export function withLocale(loc, path) {
  const n = normalize(loc) || DEFAULT_LOCALE;
  const clean = stripLocaleFromPath(path || "/");
  return `/${n}${clean === "/" ? "" : clean}` || `/${n}`;
}

function fromAcceptLanguage() {
  if (typeof navigator === "undefined") return null;
  const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  for (const l of langs || []) {
    const n = normalize(l);
    if (n) return n;
  }
  return null;
}

async function fromGeo() {
  try {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return null;
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 3500);
    const res = await fetch(`${backend}/api/public/geo`, { signal: controller.signal });
    clearTimeout(t);
    if (!res.ok) return null;
    const data = await res.json();
    const c = String(data.country || "").toUpperCase();
    return (c === "BR" || c === "PT") ? "pt" : "en";
  } catch {
    return null;
  }
}

/**
 * Resolves the locale following the exact priority:
 * cookie -> URL -> Accept-Language -> Geo -> "pt".
 * Returns { locale, source } so callers know why it resolved that way.
 */
export async function resolveLocale({ pathname } = {}) {
  // 1) cookie (also migrate legacy localStorage on the fly)
  const cookie = getCookieLocale();
  if (cookie) return { locale: cookie, source: "cookie" };
  try {
    const legacy = normalize(localStorage.getItem(LEGACY_LS_KEY));
    if (legacy) {
      setCookieLocale(legacy);
      return { locale: legacy, source: "legacy" };
    }
  } catch { /* noop */ }

  // 2) URL prefix
  const url = getLocaleFromPath(pathname || (typeof window !== "undefined" ? window.location.pathname : ""));
  if (url) return { locale: url, source: "url" };

  // 3) Accept-Language
  const nav = fromAcceptLanguage();
  if (nav) return { locale: nav, source: "navigator" };

  // 4) Geo IP
  const geo = await fromGeo();
  if (geo) return { locale: geo, source: "geo" };

  // 5) fallback
  return { locale: DEFAULT_LOCALE, source: "default" };
}
